import { z } from "zod";
import { nullableText, uuidListSchema, versionSchema } from "./common.js";

export const ORGANIZATION_UNIT_KINDS = [
  "company",
  "department",
  "team",
] as const;
export type OrganizationUnitKind = (typeof ORGANIZATION_UNIT_KINDS)[number];

const teamNameSchema = z.string().trim().min(1).max(120);
const teamColorSchema = z.preprocess(
  (value) => {
    if (typeof value !== "string") return value;
    const normalized = value.trim();
    return normalized.length === 0 ? null : normalized;
  },
  z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .nullable()
    .optional(),
);

export const teamCreateSchema = z
  .object({
    name: teamNameSchema,
    description: nullableText(2000),
    color: teamColorSchema,
    kind: z.enum(ORGANIZATION_UNIT_KINDS).default("team"),
    parentId: z.string().uuid().nullable().default(null),
    sortOrder: z.number().int().min(0).max(100_000).default(0),
    active: z.boolean().default(true),
    memberIds: uuidListSchema.default([]),
    /** Members shown as leads of the unit (organisational only). */
    leadIds: uuidListSchema.default([]),
  })
  .strict();
export type TeamCreateDto = z.infer<typeof teamCreateSchema>;

export const teamUpdateSchema = teamCreateSchema
  .partial()
  .extend({ version: versionSchema })
  .strict();
export type TeamUpdateDto = z.infer<typeof teamUpdateSchema>;

export const teamMembersUpdateSchema = z
  .object({
    version: versionSchema,
    memberIds: uuidListSchema,
    leadIds: uuidListSchema.default([]),
  })
  .strict();
export type TeamMembersUpdateDto = z.infer<typeof teamMembersUpdateSchema>;

export const teamStatusSchema = z
  .object({
    version: versionSchema,
    active: z.boolean(),
  })
  .strict();
export type TeamStatusDto = z.infer<typeof teamStatusSchema>;

export const teamListQuerySchema = z
  .object({
    search: z.string().trim().max(120).optional(),
    active: z.coerce.boolean().optional(),
  })
  .strict();
export type TeamListQueryDto = z.infer<typeof teamListQuerySchema>;
